import { Dispatch } from 'redux';
import { History } from 'history';
import { values } from 'lodash';
import { State, StoryQuery } from '../state';
import * as selectors from '../selectors';
import { Routes, getCompetitionNameParams, resolveCompetitionName, sanitizeCompetitionName, persistCompetitionSlug, resolveCompetitionSlug } from '../../utils/routerHelper';
import { patch } from './patch';
import { ActionType } from './types';
// import { competitionName } from '../../utils/userApiHelper';
import { handleUserApiError, handleUnexpectedError } from './handleUnexpectedError';
import { ExtraArguments } from '../extraArguments';
import { refreshCompetitionFeed, normalizeStoryFeedPayload, mergeStoriesNewestFirst } from './story';
import { postAccountVerification } from './userEmail';
import { Competition, CompetitionPledges, CompetitionScore } from '../../userApi';
import { sleep } from '../../utils/sleep';
import { intercom } from '../../utils/intercom';
import { trackEvent, setAttribute } from '../../utils/googleAnalytics';
import logger from '../../utils/logger';

const indexPledgeIDsByUUID = (pledges: CompetitionPledges) =>
  values(pledges).reduce((previous, pledge) => ({ ...previous, [pledge.uuid]: pledge.id }), {});

const repairInvalidCompetitionPath = (history: History, competitionName: string) => {
  const pathSegment = history.location.pathname.split('/').filter(Boolean)[0];
  if (pathSegment === competitionName) {
    return;
  }
  if (!pathSegment || pathSegment === 'undefined' || pathSegment === 'null') {
    const suffix = history.location.pathname.replace(/^\/[^/]*/, '');
    history.replace(`/${competitionName}${suffix || ''}`);
  }
};

const clearPersistedSession = (competitionName?: string) => {
  localStorage.removeItem('redux');
  localStorage.removeItem('competitionName');
  persistCompetitionSlug(competitionName);
};

export const initialize = () => async (dispatch: Dispatch, getState: () => State, extra: ExtraArguments) => {
  const state = getState();
  const api = selectors.userApi(state);
  const { i18n, history } = extra;
  const params = getCompetitionNameParams(history.location, i18n);
  if (params == 'GLOBAL') {
    dispatch(patch({
      spinner: false,
      initialized: true
    }, ActionType.Initialize));
    return;
  }

  // if (state.initialQuery.impersonate_token) {
  //  state.token = state.initialQuery.impersonate_token;
  // }

  const competitionName = resolveCompetitionName(history.location, i18n, state.initialQuery);
  if (competitionName) {
    persistCompetitionSlug(competitionName);
    dispatch(patch({ spinner: true, initialQuery: { competitionName } }));
  } else {
    localStorage.removeItem('competitionName');
    dispatch(patch({ spinner: true }));
  }

  if (state.initialQuery.verificationToken) {
    await postAccountVerification()(dispatch, getState, extra);
    await sleep(1000);
  }
  let mockCompetition = false;
  if (!competitionName) {
    // return handleUnexpectedError({}, {message:"Joining a campus competition? Enter the url in this format https://www.coolcampuschallenge.org/&lt;competitionName&gt;/<br/>If you are looking for more info please visit <a href='https://www.coolcampuschallenge.org/about' target='_blank'>https://www.coolcampuschallenge.org/about</a>", persistent:true})(dispatch, getState);
    mockCompetition = true;
    const newState = {
      spinner: false,
      initialized: true,
      competition: { logoUrl: '', config: { campusLabel: '' } } as Competition
      // competitionScore: { savingsCO2Lbs: 0, participants: 0, pledgesCompleted: 0 } as CompetitionScore
    };
    dispatch(patch(newState, ActionType.Initialize));
    return;
  }

  try {
    const loadLoggedOutDashboard = async () => {
      if (!competitionName) {
        throw new Error('Missing competition name for dashboard load');
      }
      const competitionDashboard = await api.getCompetitionDashboard(competitionName);
      trackEvent('app', 'initialize', 'loggedOut');
      dispatch(patch({
        ...competitionDashboard,
        competitionPledgeIDsByUUID: indexPledgeIDsByUUID(competitionDashboard.competitionPledges),
        token: '',
        user: undefined,
        competitionUser: undefined,
        spinner: false,
        initialized: true
      }, ActionType.Initialize));
      if (competitionDashboard.competition && competitionDashboard.competition.name) {
        persistCompetitionSlug(competitionDashboard.competition.name);
        repairInvalidCompetitionPath(history, competitionDashboard.competition.name);
      }
    };

    if (selectors.isLoggedIn(state)) {
      try {
        if (!competitionName) {
          throw new Error('Missing competition name for authenticated initialize');
        }
        // Progressive paint: anonymous dashboard is ~0.15s (HIT-EARLY). Show the
        // shared shell while authenticated /me (~seconds) finishes.
        let paintedShell = false;
        const anonApi = selectors.userApi({ ...state, token: '' } as State);
        const anonPaint = anonApi.getCompetitionDashboard(competitionName)
          .then((competitionDashboard) => {
            if (paintedShell || getState().initialized) {
              return;
            }
            paintedShell = true;
            dispatch(patch({
              ...competitionDashboard,
              competitionPledgeIDsByUUID: indexPledgeIDsByUUID(competitionDashboard.competitionPledges),
              // Keep session credentials; user-specific fields arrive from getMe.
              token: state.token,
              spinner: true,
              initialized: true
            }, ActionType.Initialize));
            if (competitionDashboard.competition && competitionDashboard.competition.name) {
              persistCompetitionSlug(competitionDashboard.competition.name);
              repairInvalidCompetitionPath(history, competitionDashboard.competition.name);
            }
          })
          .catch(() => { /* ignore — getMe remains source of truth */ });

        const userDashboard = await api.getMe(undefined, competitionName);
        if (!userDashboard.competitionUser) {
          throw new Error('Authenticated dashboard missing competitionUser');
        }
        paintedShell = true;
        const newState = {
          ...userDashboard,
          competitionPledgeIDsByUUID: indexPledgeIDsByUUID(userDashboard.competitionPledges),
          spinner: false,
          initialized: true
        };
        if (mockCompetition) {
          newState.competition = undefined;
          console.log(newState);
        }
        dispatch(patch(newState, ActionType.Initialize));
        trackEvent('app', 'initialize', 'loggedIn');
        const initializedState = getState();
        if (initializedState.competition && initializedState.competition.name) {
          persistCompetitionSlug(initializedState.competition.name);
          repairInvalidCompetitionPath(history, initializedState.competition.name);
        }
        setAnalyticsAttributes(initializedState);
        logger.configure(initializedState);
        await anonPaint;
      } catch (err) {
        clearPersistedSession(competitionName);
        await loadLoggedOutDashboard();
      }
    } else {
      await loadLoggedOutDashboard();
    }
    await sleep(3000);
    const loggedInState = getState();
    const university = selectors.userUniversity(loggedInState);
    const { user, competitionUser } = loggedInState;
    intercom('boot', { user, university, competitionUser });
  } catch (err) {
    clearPersistedSession(competitionName || resolveCompetitionSlug());
    try {
      const competitionDashboard = await api.getCompetitionDashboard(competitionName);
      dispatch(patch({
        ...competitionDashboard,
        competitionPledgeIDsByUUID: indexPledgeIDsByUUID(competitionDashboard.competitionPledges),
        token: '',
        user: undefined,
        competitionUser: undefined,
        spinner: false,
        initialized: true
      }, ActionType.Initialize));
      if (competitionDashboard.competition && competitionDashboard.competition.name) {
        persistCompetitionSlug(competitionDashboard.competition.name);
        repairInvalidCompetitionPath(history, competitionDashboard.competition.name);
      }
    } catch (fallbackErr) {
      handleUserApiError(fallbackErr)(dispatch, getState);
      dispatch(patch({
        token: '',
        user: undefined,
        competitionUser: undefined,
        spinner: false,
        initialized: true
      }, ActionType.Initialize));
    }
    await sleep(3000);
    intercom('boot');
  }
};

export const reinitializeDashboardIfNecessary = () => async (dispatch: Dispatch, getState: () => State, { i18n }: ExtraArguments) => {
  const { competition } = getState();
  const competitionStoryQuery: StoryQuery = {
    competitionPledgeID: 0,
    entityID: 0,
    userID: 0,
    storyID: 0
  };

  // Keep just-posted stories across reload; do NOT clear the feed before fetch
  // (clearing first caused "No activity in this challenge" when getMe raced).
  const retainedStories = (getState().stories || []).slice(0, 5);

  dispatch(patch({ spinner: true, storyQuery: competitionStoryQuery }));
  const t = i18n.getFixedT(i18n.language, 'pledge');

  try {
    const state = getState();
    const api = selectors.userApi(state);
    const res = await api.getMe(undefined, competition.name);
    const { pendingRoute } = getState();
    const serverStories = (res && res.stories) || [];
    const mergedStories = mergeStoriesNewestFirst(retainedStories, serverStories);

    dispatch(patch({
      ...res,
      ...normalizeStoryFeedPayload({ ...res, stories: mergedStories }, getState()),
      competitionPledgeIDsByUUID: indexPledgeIDsByUUID(res.competitionPledges),
      spinner: false,
      pendingRoute: pendingRoute === Routes.Dashboard ? null : pendingRoute
    }));

    // Belt-and-suspenders: /stories is the reliable feed source (getMe can race).
    await refreshCompetitionFeed()(dispatch, getState);
  } catch (err) {
    const { pendingRoute } = getState();
    dispatch(patch({
      spinner: false,
      pendingRoute: pendingRoute === Routes.Dashboard ? null : pendingRoute
    }));
    // Still try the dedicated feed endpoint so Home is not left empty.
    try {
      await refreshCompetitionFeed()(dispatch, getState);
    } catch (_feedErr) {
      // ignore — surfaced below
    }
    handleUnexpectedError(err, {
      title: t('Oops!'),
      message: t('We experienced an error retrieving stories.')
    })(dispatch, getState);
  }
};

export const setAnalyticsAttributes = (state: State) => {
  const { user } = state;
  if (user && user.type != null && user.type !== '') {
    setAttribute('userType', String(user.type));
  }
  const campus = selectors.userUniversity(state);
  if (campus && campus.name) {
    setAttribute('campus', campus.name);
  }
  const team = selectors.userTeam(state);
  if (team && team.name) {
    setAttribute('team', team.name);
  }
};
